import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import { analytics, EventEnum } from "@/lib/analytics";
import type { ListCreatorDto } from "@/lib/lutely-api/gen/dtos";
import { cn } from "@/lib/utils";
import {
  Button,
  Flex,
  Box,
  IconButton,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import AccountProfile from "./AccountProfile";
import ProfileMenu from "./ProfileMenu";
import ActivityTracker from "./ActivityTracker";
import CartButton from "./CartButton";
import { MarketingNavLinks, MarketingNavMenu } from "./MarketingNav";
import SearchField from "./SearchField";

import LabIcon from "@/assets/icons/Lab  science experiment 2.svg?react";
import MarketplaceIcon from "@/assets/icons/marketplace filled.svg?react";

import { SearchIcon } from "@/assets/icons";
import LogoIcon from "@/assets/lutely-icon.svg";
import { Link, useMatchRoute, useRouterState } from "@tanstack/react-router";
import { IoMdClose } from "react-icons/io";
import { FiHelpCircle } from "react-icons/fi";
import { useUserback } from "@/hooks/useUserback";
import { useSearchResults } from "@/hooks/useSearchResults";
import SearchDropdown from "./SearchDropdown";

export default function HeaderNav() {
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState("");
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { open: openSupport } = useUserback();

  // Mobile search queries
  const {
    albums: mobileAlbums,
    songs: mobileSongs,
    creators: mobileCreators,
    hasResults: mobileHasResults,
    isLoading: mobileIsLoading,
    canSearch: canMobileSearch,
  } = useSearchResults(mobileSearchValue, { enabled: mobileSearchOpen });

  const isHomePage = matchRoute({ to: "/" });
  const isMarketplacePage = matchRoute({ to: "/marketplace", fuzzy: true });
  const isAudiolabPage = matchRoute({ to: "/audiolab", fuzzy: true });

  const isDarkHeader = Boolean(isHomePage);
  const marketplaceSearchTarget =
    pathname === "/marketplace/songs" || pathname === "/marketplace/albums"
      ? pathname
      : "/marketplace/albums";

  const { data: user } = useLoggedInUser();
  const isAuthenticated = user != null;

  const openMobileSearch = () => {
    setMobileSearchOpen(true);
  };

  const closeMobileSearch = () => {
    setMobileSearchOpen(false);
    setMobileSearchValue("");
  };

  const mobileGoTo = (to: string, params?: Record<string, string>) => {
    closeMobileSearch();
    navigate({ to, params });
  };

  // Branded /@slug first; the raw id URL only exists for creators without a
  // slug (LUT-927).
  const handleMobileSelectCreator = (creator: ListCreatorDto) => {
    if (creator.slug) {
      mobileGoTo("/@{$CREATOR_SLUG}", { CREATOR_SLUG: creator.slug });
    } else {
      mobileGoTo("/creators/$CREATOR_ID", { CREATOR_ID: creator.creatorId });
    }
  };

  const handleMobileSelectAlbum = (albumId: string) => {
    mobileGoTo("/albums/$ALBUM_ID", { ALBUM_ID: albumId });
  };

  const handleMobileSelectSong = (trackId: string) => {
    mobileGoTo("/tracks/$TRACK_ID", { TRACK_ID: trackId });
  };

  useEffect(() => {
    if (mobileSearchOpen && mobileSearchInputRef.current) {
      mobileSearchInputRef.current.focus();
    }
  }, [mobileSearchOpen]);

  const handleMobileSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && mobileSearchValue.trim()) {
      analytics.track(EventEnum.MARKETPLACE_FILTER_SELECTED, {
        searchQuery: mobileSearchValue,
      });
      navigate({
        to: marketplaceSearchTarget,
        search: { query: mobileSearchValue },
      });
      closeMobileSearch();
    }
    if (e.key === "Escape") {
      closeMobileSearch();
    }
  };

  return (
    <>
      <Flex
        className={cn(
          "w-full border-b",
          isDarkHeader
            ? "border-transparent bg-blue-900 text-white"
            : "bg-background-primary text-text-secondary border-[rgba(5,12,43,0.08)]",
        )}
      >
        <ContainerWrapper
          className={cn(
            "flex h-[72px] items-center justify-center text-black",
            isDarkHeader && "text-white fill-white",
          )}
        >
          {/* Mobile search - replaces header content in place */}
          {mobileSearchOpen ? (
            <Box className="relative w-full md:hidden">
              <Flex className="w-full items-center gap-3">
                <Flex
                  className={cn(
                    "flex-1 h-12 items-center gap-3 rounded-full px-4",
                    isDarkHeader
                      ? "bg-[rgba(255,255,255,0.1)] text-white"
                      : "border border-tertiary text-slate-900",
                  )}
                >
                  <SearchIcon
                    className={cn(
                      "w-5 flex-shrink-0",
                      isDarkHeader ? "text-white" : "text-text-secondary",
                    )}
                    fill="currentColor"
                  />
                  <TextField.Root
                    ref={mobileSearchInputRef}
                    className={cn(
                      "flex-1 h-10 border-0 bg-transparent text-[16px] font-semibold outline-none shadow-none",
                      isDarkHeader ? "sOutlinedDark" : "sOutlined",
                    )}
                    placeholder="Search artists or tracks"
                    variant="soft"
                    aria-label="Search artists or tracks"
                    value={mobileSearchValue}
                    onKeyDown={handleMobileSearchKeyDown}
                    onChange={(e) => setMobileSearchValue(e.target.value)}
                    inputMode="search"
                    autoComplete="off"
                  />
                  {mobileSearchValue && (
                    <IconButton
                      variant="ghost"
                      size="2"
                      onClick={() => setMobileSearchValue("")}
                      className={cn(
                        "cursor-pointer",
                        isDarkHeader ? "text-white" : "text-text-secondary",
                      )}
                      aria-label="Clear search"
                    >
                      <IoMdClose size={18} />
                    </IconButton>
                  )}
                </Flex>
                <IconButton
                  variant="ghost"
                  size="3"
                  onClick={closeMobileSearch}
                  className={cn(
                    "cursor-pointer",
                    isDarkHeader ? "text-white" : "text-text-primary",
                  )}
                  aria-label="Close search"
                >
                  <IoMdClose size={24} />
                </IconButton>
              </Flex>

              {/* Mobile search dropdown */}
              <SearchDropdown
                show={canMobileSearch}
                albums={mobileAlbums}
                songs={mobileSongs}
                creators={mobileCreators}
                hasResults={mobileHasResults}
                isLoading={mobileIsLoading}
                onSelectCreator={handleMobileSelectCreator}
                onSelectAlbum={handleMobileSelectAlbum}
                onSelectSong={handleMobileSelectSong}
              />
            </Box>
          ) : (
            <Flex className="w-full items-center gap-2 lg:gap-6">
              {/* Left: Logo */}
              <Flex className="flex-1 gap-[32px]">
                <Flex className="items-center">
                  <Link to="/" className="flex items-center gap-1 min-w-0">
                    <img
                      src={LogoIcon}
                      alt="Lutely Icon"
                      className="w-7 flex-shrink-0"
                    />
                    {/* Icon only on mobile: the wordmark's width is needed for
                        the section icons beside it. */}
                    <Text
                      className={cn(
                        "hidden md:inline text-[24px] font-righteous leading-none cursor-pointer",
                        isDarkHeader ? "text-white" : "text-blue-900",
                      )}
                    >
                      Lutely
                    </Text>
                  </Link>
                </Flex>

                {/* Mobile: the two sections as icons. Desktop has them with
                    labels in the centre nav below, which is hidden here. */}
                <Flex className="items-center gap-[18px] md:hidden">
                  <Link to="/marketplace" aria-label="Marketplace">
                    <MarketplaceIcon
                      width={22}
                      height={22}
                      className={cn(
                        isMarketplacePage
                          ? "fill-brand-primary"
                          : isDarkHeader
                            ? "fill-white/70"
                            : "fill-text-secondary",
                      )}
                    />
                  </Link>
                  {isAuthenticated && (
                    <Link to="/audiolab" aria-label="Audiolab">
                      <LabIcon
                        width={22}
                        height={22}
                        className={cn(
                          isAudiolabPage
                            ? "fill-brand-primary"
                            : isDarkHeader
                              ? "fill-white/70"
                              : "fill-text-secondary",
                        )}
                      />
                    </Link>
                  )}
                </Flex>

                {/* Center: Desktop nav + search */}
                <Flex
                  className={cn(
                    "hidden max-w-[650px] h-10 w-full md:flex items-center justify-center gap-[14px] lg:gap-[24px]",
                    !isAuthenticated && "lg:max-w-[860px]",
                  )}
                >
                  <Link to="/marketplace">
                    <Flex
                      className={cn(
                        "gap-1 font-semibold text-[14px]",
                        isDarkHeader
                          ? "text-white/70 fill-white/70 hover:text-white hover:fill-white"
                          : "text-[#525966] hover:text-brand-primary hover:fill-brand-primary",
                        isMarketplacePage
                          ? "text-brand-primary fill-brand-primary"
                          : isDarkHeader
                            ? ""
                            : "fill-text-secondary",
                      )}
                    >
                      <MarketplaceIcon width={22} height={22} />
                      <Text>Marketplace</Text>
                    </Flex>
                  </Link>

                  {isAuthenticated && (
                    <Link to="/audiolab">
                      <Flex
                        className={cn(
                          "gap-1 font-semibold text-[14px]",
                          isDarkHeader
                            ? "text-white/70 fill-white/70 hover:text-white hover:fill-white"
                            : "text-[#525966] hover:text-brand-primary hover:fill-brand-primary",
                          isAudiolabPage
                            ? "text-brand-primary fill-brand-primary"
                            : isDarkHeader
                              ? ""
                              : "fill-text-secondary",
                        )}
                      >
                        <LabIcon width={22} height={22} />
                        <Text>Audiolab</Text>
                      </Flex>
                    </Link>
                  )}
                  <SearchField isDark={isDarkHeader} />
                  {!isAuthenticated && (
                    <Flex className="hidden lg:flex items-center gap-[24px]">
                      <MarketingNavLinks isDark={isDarkHeader} />
                    </Flex>
                  )}
                </Flex>
              </Flex>

              {/* Right: Cart, Auth, Hamburger */}
              <Flex className="items-center justify-end gap-[16px]">
                {/* The search is the marketplace's, so it has no place on the
                    artist dashboard — mobile only, where space is tight. */}
                {!isAudiolabPage && (
                  <IconButton
                    className={cn(
                      "md:hidden cursor-pointer",
                      isDarkHeader
                        ? "text-white/70 hover:text-white"
                        : "text-text-secondary hover:text-black",
                    )}
                    variant="ghost"
                    size="3"
                    onClick={openMobileSearch}
                    aria-label="Open search"
                  >
                    {/* fill-current, not just the text color: Search.svg
                        carries no fill of its own, so without this it ignores
                        the class above and takes whatever fill it inherits —
                        the container's white on the dark header, the browser's
                        black on the light one. It was the one icon in this row
                        that didn't match its neighbours. */}
                    <SearchIcon className="w-5 h-5 fill-current" />
                  </IconButton>
                )}

                <ActivityTracker isDark={isDarkHeader} />

                {/* Every viewport: with Get Help gone from the sidebar and
                    the mobile More menu (8/14 notes), this icon is the one
                    remaining support entry point. */}
                <Tooltip content="Support">
                  <IconButton
                    className={cn(
                      "inline-flex cursor-pointer",
                      isDarkHeader
                        ? "text-white/70 hover:text-white"
                        : "text-[#525966] hover:text-black",
                    )}
                    variant="ghost"
                    size="3"
                    onClick={openSupport}
                    aria-label="Support"
                  >
                    <FiHelpCircle size={20} />
                  </IconButton>
                </Tooltip>

                {/* Cart - always visible for all users */}
                <Tooltip content="Cart">
                  <Box
                    className={cn(
                      isDarkHeader
                        ? "fill-white/70 hover:fill-white"
                        : "fill-text-secondary hover:fill-black",
                    )}
                  >
                    <CartButton />
                  </Box>
                </Tooltip>

                {isAuthenticated ? (
                  <>
                    <Flex className="hidden md:block">
                      <AccountProfile />
                    </Flex>
                    {/* Mobile: the same profile menu on every page — the
                        hamburger and its overlay are gone, so the avatar is
                        the one account entry point. */}
                    <Flex className="md:hidden">
                      <ProfileMenu testId="artist-selector-mobile" />
                    </Flex>
                  </>
                ) : (
                  <>
                    {/* Below sm it moves into the menu. */}
                    <button
                      type="button"
                      onClick={() =>
                        navigate({
                          to: ".",
                          search: (prev) => ({
                            ...prev,
                            auth: "login" as const,
                          }),
                        })
                      }
                      className={cn(
                        "hidden sm:inline-flex cursor-pointer mr-1 whitespace-nowrap",
                        isDarkHeader
                          ? "text-white/70 hover:text-white"
                          : "text-[#525966] hover:text-blue-600",
                      )}
                    >
                      <Text className="font-semibold  text-[14px]">
                        Sign in
                      </Text>
                    </button>

                    <Button
                      type="button"
                      variant="ghost"
                      radius="full"
                      onClick={() =>
                        navigate({
                          to: ".",
                          search: (prev) => ({
                            ...prev,
                            auth: "signup" as const,
                          }),
                        })
                      }
                      // Visible on mobile too: the hamburger that used to
                      // carry Sign up there is gone, and the auth modal's
                      // internal switch shouldn't be the only path in.
                      className="font-bold text-[14px] h-[34px] text-blue-900 bg-brand-secondary hover:bg-teal-600 px-3 md:px-4 mr-2"
                    >
                      Sign up
                    </Button>
                    <Flex className="lg:hidden">
                      <MarketingNavMenu isDark={isDarkHeader} />
                    </Flex>
                  </>
                )}
              </Flex>
            </Flex>
          )}
        </ContainerWrapper>
      </Flex>
    </>
  );
}
